import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncFn } from 'react-use';
import { replaceMoodleTrainingPartLanguageLinks } from 'http/moodle';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { routesAliases, buildProvisionedModuleEditPath, buildProvisionedTrainingPartEditPath } from 'router/routesAliases';
import { useTranslation } from 'react-i18next';
import TrainingPartForm from 'pages/TrainingModules/Edit/TrainingPartForm';
import {
  createProvisionedTrainingPart,
  deleteProvisionedTrainingPart,
  getPreviousProvisionedTrainingPart,
  getProvisionedTrainingModule,
} from 'http/hyperAdminProvisioned';

const TRAINING_METHODS = ['theory', 'ojt', 'practical'];

const METHOD_TAB_INDEX = {
  theory: 1,
  practical: 2,
  ojt: 3,
};

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(5, 5),
  },
}));

const CreatePart = ({ routeProps }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation('common');
  const { companyId, moduleId, trainingMethod } = routerMatch.params;
  const [notFoundError, setNotFoundError] = useState(false);
  const [moduleData, setModuleData] = useState(null);
  const [previousTrainingPart, setPreviousTrainingPart] = useState(null);

  const backUrl = buildProvisionedModuleEditPath(companyId, moduleId);
  const backState = { activeTab: METHOD_TAB_INDEX[trainingMethod] ?? 0, returnTab: location.state?.returnTab };

  const isValidMethod = TRAINING_METHODS.includes(trainingMethod);

  const [fetchState, fetchModule] = useAsyncFn(async () => {
    const [moduleResult, previousResult] = await Promise.all([
      getProvisionedTrainingModule(Number(companyId), moduleId),
      getPreviousProvisionedTrainingPart(Number(companyId), moduleId, trainingMethod),
    ]);

    if (!moduleResult.error) {
      setModuleData(moduleResult);
      setNotFoundError(false);
    } else if (moduleResult.statusCode === 404) {
      setNotFoundError(true);
    }

    if (!previousResult?.error && !previousResult?.statusCode) {
      setPreviousTrainingPart(previousResult?.items?.[0] || null);
    } else {
      setPreviousTrainingPart(null);
    }

    return moduleResult;
  }, [companyId, moduleId, trainingMethod]);

  useEffect(() => {
    if (isValidMethod) {
      setNotFoundError(false);
      setModuleData(null);
      setPreviousTrainingPart(null);
      fetchModule();
    }
  }, [companyId, moduleId, trainingMethod, isValidMethod]);

  const breadCrumbsItems = useMemo(
    () => [
      {
        title: t(routeProps.parentTitle),
        link: routeProps.parentPath,
      },
      {
        title: t('routes.editCompany'),
        link: `${routesAliases.editCompany}/${companyId}`,
      },
      {
        title: moduleData?.name || t('routes.editTrainingModule'),
        link: { pathname: backUrl, state: backState },
      },
      {
        title: t('trainingParts.addPageTitle'),
        isActive: true,
      },
    ],
    [backState, backUrl, companyId, moduleData?.name, routeProps.parentPath, routeProps.parentTitle, t],
  );

  const [submittingState, createPart] = useAsyncFn(
    async payload => {
      const { moodleLanguageLinks, ...partPayload } = payload;
      const result = await createProvisionedTrainingPart(Number(companyId), {
        ...partPayload,
        trainingModuleId: Number(moduleId),
      });

      if (result.error || result.statusCode) {
        return result;
      }

      if (trainingMethod === 'theory' && moodleLanguageLinks?.variants?.length) {
        const linkResult = await replaceMoodleTrainingPartLanguageLinks(result.id, moodleLanguageLinks);
        if (linkResult?.error || linkResult?.statusCode) {
          await deleteProvisionedTrainingPart(Number(companyId), result.id);
          return linkResult;
        }
      }

      history.push(
        buildProvisionedTrainingPartEditPath(companyId, moduleId, result.id, trainingMethod),
        backState,
      );
      return result;
    },
    [backState, companyId, history, moduleId, trainingMethod],
  );

  if (!isValidMethod || notFoundError) {
    return <NotFound />;
  }

  return (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchState.loading || !moduleData}
    >
      {moduleData ? (
        <TrainingPartForm
          mode="create"
          layout="page"
          trainingMethod={trainingMethod}
          trainingModuleCode={moduleData.code}
          previousTrainingPart={previousTrainingPart}
          backUrl={backUrl}
          backState={backState}
          submitting={submittingState.loading}
          onSubmitForm={createPart}
          isReadOnly={false}
        />
      ) : (
        <span />
      )}
    </PageLayout>
  );
};

CreatePart.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default CreatePart;
